/**
 * D-EZ-101 / D-EZ-103 — the Settings panel behind the strip's "Settings" button.
 *
 * Two things:
 *
 *   • LEAGUE SETTINGS (D-EZ-103) — the rules the league is raced under, read-only,
 *     with "Edit for next season". A season under way keeps the rules it began
 *     with (changing them mid-year would make race 12 and race 13 two different
 *     games); an edit lands on the next season the league starts.
 *   • "Show rulebook citations" (D-EZ-101), off by default.
 *
 * Dice motion and sound stay on the dice table, where they are used.
 */

import { useState } from 'react';
import { CitationsSwitch } from './Cite.tsx';
import { RealismRules, ScoringRules, SettingsReadout } from './LeagueSettingsForm.tsx';
import { sameSettings, type LeagueSettings } from '../lib/leagueSettings.ts';

export interface LeagueSettingsView {
  leagueName: string;
  /** The rules the season on the workbench is raced under (live or last settled). */
  current: LeagueSettings;
  /** "Season 2 — being raced" / "Season 2 — settled". */
  currentLabel: string;
  /** What the next season will start with. */
  next: LeagueSettings;
  nextSeasonNumber: number;
  onNextChange: (next: LeagueSettings) => void;
}

export default function SettingsPanel({ league = null }: { league?: LeagueSettingsView | null }) {
  return (
    <section className="prefs" aria-label="Settings">
      {league && <LeagueSettingsPanel league={league} />}
      <h2 className="prefs__h">Display</h2>
      <CitationsSwitch />
    </section>
  );
}

export function LeagueSettingsPanel({ league }: { league: LeagueSettingsView }) {
  const [editing, setEditing] = useState(false);
  const changed = !sameSettings(league.current, league.next);
  return (
    <div className="leaguerules">
      <h2 className="prefs__h">League settings · {league.leagueName}</h2>
      <p className="leaguerules__where">{league.currentLabel}</p>
      <SettingsReadout settings={league.current} />
      {changed && !editing && (
        <p className="leaguerules__next">
          <b>Season {league.nextSeasonNumber} will use different rules:</b>
        </p>
      )}
      {changed && !editing && <SettingsReadout settings={league.next} onlyChangedFrom={league.current} />}
      {editing ? (
        <div className="leaguerules__edit">
          <p className="leaguerules__where">
            Changes apply from season {league.nextSeasonNumber}. This season keeps the rules it started with.
          </p>
          <RealismRules
            value={league.next.race}
            onChange={(race) => league.onNextChange({ ...league.next, race })}
            idPrefix="ls-race"
          />
          <ScoringRules
            value={league.next.sheet}
            onChange={(sheet) => league.onNextChange({ ...league.next, sheet })}
            idPrefix="ls-sheet"
          />
          <div className="leaguerules__acts">
            <button type="button" className="btn btn--hot leaguerules__btn" onClick={() => setEditing(false)}>
              Done
            </button>
            {changed && (
              <button
                type="button"
                className="btn btn--quiet leaguerules__btn"
                onClick={() => league.onNextChange(league.current)}
              >
                Keep this season&rsquo;s rules
              </button>
            )}
          </div>
        </div>
      ) : (
        <button type="button" className="btn btn--quiet leaguerules__btn" onClick={() => setEditing(true)}>
          Edit for next season
        </button>
      )}
    </div>
  );
}
